import { useEffect, useMemo, useRef } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { createSelector } from '@reduxjs/toolkit'

import { newUuid, useOpenWindow, useWindow } from '@krp/client-runtime'
import { platformMyResources } from '@krp/platform-ui'
import {
  AQSI_WINDOWS,
  aqsiReceiptJournal,
  aqsiReceiptRegisters,
  aqsiReceiptShifts,
  aqsiReceiptSummary,
  aqsiReceipts,
  aqsiMySession,
  aqsiParkedSales,
  aqsiSalesAttention,
  createJournalViewActions,
  createReceiptsTabActions,
  type ReceiptsTab,
  cursorOf,
  draftChanged,
  effectiveFilter,
  filterApplied,
  filterReset,
  journalSyncRequested,
  listShownOf,
  listRestarted,
  makeSelectReceiptsVm,
  pageAdvanced,
  pageReturned,
  receiptsReloadRequested,
  ReceiptsScreen,
  RefundSearchScreen,
  SalesStatisticsScreen,
  selectReceiptsView,
  selectSyncFailure,
  summaryFilterOf,
  summaryRangeOf,
  summaryUsesMonth,
  FEEDBACK_SCOPES,
  mineSeatOf,
  ridKey,
  saleUnparkRequested,
  type ReceiptListArgs,
  type ReceiptsActions,
} from '@krp/resource-aqsi-ui'
import { firstOfMonth, isoDayOf, lastOfMonth } from '@krp/ui-kit'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { AQSI_MINE } from './aqsiMine'
import { ReceiptLinesBinding } from './ReceiptLinesBinding'

/** Пока сверка журнала идёт, статус перечитывается сам; иначе — по запросу экрана. */
const JOURNAL_POLL_MS = 5000
/** «Требуют внимания»: число в названии вкладки обновляется само, без F5 (RP-FB35 K7). */
const ATTENTION_POLL_MS = 30_000

/**
 * Binding окна «Чеки» ресурса AQSI: rid берётся из контекста окна (адрес или
 * `?w=`), а не из глобальной настройки. Args стабильны и совпадают у подписки и
 * `select` — иначе selector читал бы чужой ключ кэша. Отбор уходит в службу в
 * «действующем» виде: пустые даты — сегодня по часам человека (`effectiveFilter`).
 *
 * RP-FB74: одна связка на обе вкладки. «Чеки» (`statistics` выключено) месячную сводку для графика не читает и сводок не
 * показывает; «Статистика по продажам» (`statistics`) читает её и показывает те же чеки с итогами сверху. «Стандартный возврат»
 * из РМК (`refundSearch`, этап 2) - те же общие части без сводок, а выбор чека открывает окно возврата, а не детализацию.
 */
export function ReceiptsBinding({ tab = 'journal', statistics = false, refundSearch = false }: { tab?: ReceiptsTab; statistics?: boolean; refundSearch?: boolean } = {}) {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = window.params.rid ?? ''
  const skip = resourceId === ''
  const today = isoDayOf(new Date())

  const storedView = useAppSelector((state: RootState) => selectReceiptsView(state.aqsi.receipts, resourceId))
  const view = useMemo(() => ({ ...storedView, tab }), [storedView, tab])
  const untouched = useAppSelector((state: RootState) => state.aqsi.receipts.byResource[resourceId] === undefined)
  const cursor = cursorOf(view)
  const registersArgs = useMemo(() => ({ resourceId }), [resourceId])
  const registersQuery = aqsiReceiptRegisters.useQuery(registersArgs, { skip })
  const effective = useMemo(() => {
    const filter = effectiveFilter(view.applied, today)
    const registers = registersQuery.data?.registers
    if (registers === undefined || (filter.registerId === '' && filter.shop === '')) return filter
    const serials = registers.filter((item) => filter.registerId !== '' ? item.id === filter.registerId : item.shop === filter.shop)
      .map((item) => item.serialNumber).filter((serial) => serial !== '').sort()
    return { ...filter, serials: serials.length === 0 ? '__no_registers__' : serials.join(',') }
  }, [view.applied, today, registersQuery.data])
  const listArgs = useMemo<ReceiptListArgs>(() => ({ resourceId, filter: effective, cursor }), [resourceId, effective, cursor])
  // Чеки читаются за один день или смену; период из нескольких дней — только сводка (итоги считает сервер).
  const listShown = listShownOf(view.applied, today)
  // Итоги — тот же отбор без вида чека: приход и возвраты считаются оба, чипы вида их лишь разделяют.
  const summaryArgs = useMemo(() => ({ resourceId, filter: summaryFilterOf(effective) }), [resourceId, effective])
  // График читает весь месяц отдельно: выбор дня не меняет его данные и масштаб.
  const month = firstOfMonth(effective.dateFrom || today)
  const monthArgs = useMemo(() => ({ resourceId, filter: { ...summaryFilterOf(effective),
    dateFrom: month, dateTo: lastOfMonth(month), shiftNumber: '', search: '' } }), [resourceId, effective, month])
  const useMonthSummary = summaryUsesMonth(effective)
  // Смены в дереве — у выбранной кассы.
  const shiftsArgs = useMemo(
    () => (skip || view.applied.registerId === '' ? skipToken : { resourceId, registerId: view.applied.registerId, detailsFor: view.applied.shiftNumber }),
    [skip, resourceId, view.applied.registerId, view.applied.shiftNumber],
  )

  const journalSelect = useMemo(() => aqsiReceiptJournal.select(registersArgs), [registersArgs])
  const journalActive = useAppSelector((state: RootState) => {
    const status = journalSelect(state).data?.status
    return status === 'Pending' || status === 'Running'
  })

  // Сверка закончилась — журнал изменился, и список с оговоркой источника перечитывается сам.
  const wasActive = useRef(false)
  useEffect(() => {
    if (wasActive.current && !journalActive && !skip) dispatch(receiptsReloadRequested({ resourceId }))
    wasActive.current = journalActive
  }, [journalActive, skip, dispatch, resourceId])

  // Порядок чтения: сначала то, что человек выбрал (по умолчанию — сегодняшний день): его чеки и итоги. Месяц для графика — фоном и
  // только после них; пока месяца нет, итоги берутся из самого выбранного периода.
  // Вкладка, число и итоги — по `sales.read` (список кабинетов с правом); пока список не пришёл, не запрещаем: 403 службы скажет словами.
  const readers = platformMyResources.useQuery(AQSI_MINE.salesRead)
  const attentionAllowed = readers.data === undefined || readers.data.some((source) => source.id.toLowerCase() === resourceId.toLowerCase())
  // «Обмен с AQSI» — только с правом `operations.read` на этом кабинете; пока список не пришёл, кнопки нет.
  const exchangeReaders = platformMyResources.useQuery(AQSI_MINE.exchanges)
  const exchangeAllowed = exchangeReaders.data?.some((source) => source.id.toLowerCase() === resourceId.toLowerCase()) === true
  // Итоги отдаёт ручка под `sales.read`, а чеки — под `receipts.read`: без `sales.read` итоги не запрашиваем (иначе 403), список чеков работает.
  // «Статистика по продажам» чеков не показывает — и не читает их: только итоги дня, месяца и графики.
  const dayList = aqsiReceipts.useQuery(listArgs, { skip: skip || !listShown || statistics })
  const daySummary = aqsiReceiptSummary.useQuery(summaryArgs, { skip: skip || !attentionAllowed })
  const dayDone = skip || ((!listShown || statistics || dayList.isSuccess || dayList.isError) && (!attentionAllowed || daySummary.isSuccess || daySummary.isError))
  aqsiReceiptSummary.useQuerySubscription(monthArgs, { skip: skip || !dayDone || !statistics })
  useEffect(() => {
    if (!untouched || registersQuery.data === undefined) return
    const registers = registersQuery.data.registers
    const shops = [...new Set(registers.map((item) => item.shop).filter((shop) => shop !== '' && shop !== 'Без магазина'))]
    const choose = createJournalViewActions(resourceId, dispatch).nodeChosen
    if (shops.length === 1) choose(`shop:${shops[0]!}`)
    else if (registers.length === 1) choose(`reg:${registers[0]!.id}`)
  }, [untouched, registersQuery.data, resourceId, dispatch])
  aqsiReceiptShifts.useQuerySubscription(shiftsArgs)
  aqsiSalesAttention.useQuerySubscription(registersArgs, { skip: skip || !attentionAllowed, pollingInterval: ATTENTION_POLL_MS })
  // «Отложенные продажи (деньги взяты)» — весь ресурс; «Вернуть на кассу» — на мою кассу (моя работа за кассой).
  aqsiParkedSales.useQuerySubscription(registersArgs, { skip: skip || !attentionAllowed, pollingInterval: ATTENTION_POLL_MS })
  const mySession = aqsiMySession.useQuery(skip ? skipToken : { resourceId })
  const seat = useMemo(() => mineSeatOf(mySession.data), [mySession.data])
  const seatReady = seat !== null
  const unparkFeedback = useAppSelector((state: RootState) =>
    seat === null ? undefined : state.aqsi.feedback.byKey[ridKey(resourceId, FEEDBACK_SCOPES.sale(seat.registerKey))],
  )
  aqsiReceiptJournal.useQuerySubscription(registersArgs, { skip, pollingInterval: journalActive ? JOURNAL_POLL_MS : 0 })

  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectReceiptsVm()
    const selectList = aqsiReceipts.select(listArgs)
    const selectSummary = aqsiReceiptSummary.select(summaryArgs)
    const selectMonth = aqsiReceiptSummary.select(monthArgs)
    const selectTotals = useMonthSummary ? createSelector([selectMonth, selectSummary], (monthly, day) => monthly.data === undefined ? day : ({ ...monthly,
      data: summaryRangeOf(monthly.data, effective) })) : selectSummary
    const selectRegisters = aqsiReceiptRegisters.select(registersArgs)
    const selectShifts = aqsiReceiptShifts.select(shiftsArgs)
    const selectAttention = aqsiSalesAttention.select(registersArgs)
    const selectParked = aqsiParkedSales.select(registersArgs)
    return (state: RootState) => {
      const monthly = selectMonth(state)
      const summary = selectTotals(state)
      return selectPackageVm({
        resourceId,
        view,
        today,
        list: selectList(state),
        summary,
        monthSummary: monthly,
        registers: selectRegisters(state),
        shifts: selectShifts(state),
        journal: journalSelect(state),
        syncFailure: selectSyncFailure(state.aqsi.receipts, resourceId),
        syncing: state.aqsi.receipts.syncing[resourceId] === true,
        attention: selectAttention(state),
        attentionAllowed,
        parked: selectParked(state),
        seatReady: seatReady,
        unparkFeedback,
      })
    }
  }, [useMonthSummary, effective, listArgs, monthArgs, summaryArgs, registersArgs, shiftsArgs, journalSelect, resourceId, today, attentionAllowed, seatReady, unparkFeedback, view])
  const vm = useAppSelector(selectVm)

  const actions = useMemo<ReceiptsActions>(
    () => ({
      draftChanged: (patch) => dispatch(draftChanged({ resourceId, patch })),
      apply: () => dispatch(filterApplied({ resourceId })),
      reset: () => dispatch(filterReset({ resourceId })),
      nextPage: (next) => dispatch(pageAdvanced({ resourceId, cursor: next })),
      previousPage: () => dispatch(pageReturned({ resourceId })),
      restart: () => dispatch(listRestarted({ resourceId })),
      reload: () => dispatch(receiptsReloadRequested({ resourceId })),
      // Один раз на намерение человека: повтор той же команды сервер узнаёт по этим ключам.
      syncJournal: () =>
        dispatch(journalSyncRequested({ resourceId, operationId: newUuid(), idempotencyKey: newUuid() })),
      // Продажа живёт в окне РМК этого ресурса: касса показывает свою незакрытую продажу.
      unparkSale: (saleId, expectedVersion) => {
        if (seat !== null) dispatch(saleUnparkRequested({ resourceId, registerKey: seat.registerKey, deviceId: seat.deviceId, saleId, expectedVersion }))
      },
      openSale: () => openWindow(AQSI_WINDOWS.rmk, { rid: resourceId }),
      // Что программа отправила по продаже и что AQSI ответил — отдельное окно продажи; без права `operations.read` кнопки нет.
      ...(exchangeAllowed ? { openExchange: (saleId: string) => openWindow(AQSI_WINDOWS.saleExchange, { rid: resourceId, saleId }) } : {}),
      openReceipt: (receiptId) => openWindow(refundSearch ? AQSI_WINDOWS.refund : AQSI_WINDOWS.receipt, { rid: resourceId, key: receiptId }),
      ...createJournalViewActions(resourceId, dispatch),
      chooseTab: (next) => {
        createReceiptsTabActions(resourceId, dispatch).chooseTab(next)
        openWindow(next === 'attention' ? AQSI_WINDOWS.receiptAttention : next === 'parked' ? AQSI_WINDOWS.parkedSales : AQSI_WINDOWS.receipts, { rid: resourceId })
      },
    }),
    [dispatch, exchangeAllowed, openWindow, refundSearch, resourceId, seat],
  )

  // Товары чека под раскрытой строкой журнала: тот же запрос, что у окна чека.
  const receiptLines = (receiptId: string) => <ReceiptLinesBinding resourceId={resourceId} receiptId={receiptId} />
  if (refundSearch) return <RefundSearchScreen vm={vm} actions={actions} receiptLines={receiptLines} />
  return statistics ? <SalesStatisticsScreen vm={vm} actions={actions} /> : <ReceiptsScreen vm={vm} actions={actions} sectionTabs receiptLines={receiptLines} />
}

export function SalesStatisticsBinding() { return <ReceiptsBinding statistics /> }
export function RefundSearchBinding() { return <ReceiptsBinding refundSearch /> }
export function ReceiptAttentionBinding() { return <ReceiptsBinding tab="attention" /> }
export function ParkedSalesBinding() { return <ReceiptsBinding tab="parked" /> }
